import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { decodeBilan } from "@/pages/Decouverte";
import { Sparkles, ArrowRight, Flag, TrendingUp, CalendarDays, Compass, Loader2 } from "lucide-react";

export default function MaPrepa() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [data, setData] = useState(undefined);

  useEffect(() => {
    const token = params.get("d");
    const answers = token ? decodeBilan(token) : null;
    if (!answers) { setData(null); return; }
    api.post("/public/bilan", answers).then((r) => setData(r.data)).catch(() => setData(null));
  }, [params]);

  if (data === undefined) {
    return (
      <div className="min-h-screen bg-sand flex items-center justify-center">
        <Loader2 className="animate-spin text-sage" />
      </div>
    );
  }

  if (data === null) {
    return (
      <div className="min-h-screen bg-sand flex flex-col items-center justify-center px-6 text-center">
        <h1 className="font-display text-3xl font-light tracking-tight">Ce bilan n'est plus disponible.</h1>
        <p className="mt-3 text-obsidian/60">Crée le tien en quelques minutes, gratuitement.</p>
        <button data-testid="maprepa-cta-empty" onClick={() => navigate("/decouverte")}
          className="mt-7 inline-flex items-center gap-2 rounded-full bg-obsidian text-white px-7 py-4 font-medium hover:-translate-y-0.5 transition-transform">
          Faire mon bilan gratuit <ArrowRight size={16} />
        </button>
      </div>
    );
  }

  const s = data.synthesis || {};
  return (
    <div className="min-h-screen bg-sand">
      <div className="max-w-2xl mx-auto px-5 pt-10 pb-28">
        <div className="flex items-center gap-3 mb-8">
          <img src="/logo.png" alt="MAIRUNCOACH" className="h-6 w-auto" role="button" onClick={() => navigate("/")} />
        </div>

        <div className="inline-flex items-center gap-2 text-xs font-semibold tracking-wide text-sage uppercase mb-3">
          <Sparkles size={14} /> Prépa construite par MAI
        </div>
        <h1 className="font-display text-3xl sm:text-5xl font-light tracking-tight leading-[1.05]" data-testid="maprepa-title">
          Voici ma prépa running.
        </h1>
        <p className="mt-4 text-obsidian/70 leading-relaxed">{s.orientation}</p>

        <div className="mt-8 grid sm:grid-cols-2 gap-3">
          {[
            { Icon: Flag, k: "Objectif", v: s.objectif },
            { Icon: TrendingUp, k: "Niveau estimé", v: s.niveau_estime },
            { Icon: CalendarDays, k: "Disponibilité", v: s.disponibilite },
            { Icon: Compass, k: "Programme", v: `${s.weeks_total} semaines · ${s.frequency} séances/sem.` },
          ].map(({ Icon, k, v }) => (
            <div key={k} className="rounded-2xl bg-white border border-border p-4">
              <div className="flex items-center gap-2 text-[11px] tracking-wide uppercase text-obsidian/45 font-semibold">
                <Icon size={14} className="text-sage" /> {k}
              </div>
              <div className="mt-1.5 text-[15px] font-medium text-obsidian leading-snug">{v}</div>
            </div>
          ))}
        </div>

        <div className="mt-5 rounded-2xl bg-white border border-border p-5">
          <div className="flex items-center gap-2 font-medium"><TrendingUp size={17} className="text-sage" /> Axes de progression</div>
          <ul className="mt-3 space-y-2.5">
            {(s.axes_progression || []).map((p, i) => (
              <li key={i} className="text-sm text-obsidian/70 leading-relaxed flex gap-2">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-sage shrink-0" />{p}
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="fixed bottom-0 inset-x-0 bg-white/95 backdrop-blur-md border-t border-border">
        <div className="max-w-2xl mx-auto px-5 py-4 flex flex-col sm:flex-row items-center gap-3 sm:justify-between">
          <div className="text-sm text-obsidian/70 text-center sm:text-left">
            <span className="font-medium text-obsidian">Envie de la tienne ?</span> Gratuit, sans compte, en 3 minutes.
          </div>
          <button data-testid="maprepa-cta" onClick={() => navigate("/decouverte")}
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full bg-sage text-white px-7 py-3.5 text-sm font-medium hover:bg-sage/90 hover:-translate-y-0.5 transition-[transform,background-color]">
            Faire mon bilan gratuit <ArrowRight size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}
